import React from 'react';

import axios from 'axios';

import { toast } from 'sonner';

import { TARIF_TYPES, TERRITOIRES, TYPES_ENTREPRISES } from '../GestionTarifsDemandesPubliques.constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function buildTarifsConfig(deps) {
  const { tarifs, configParTypeBySpace, activeSpaceTarif, configParEntreprise, configParTerritoire, demandes, _stats, searchTerm, selectedTerritoire, selectedType, selectedStatut, editingTarif, tarifForm, setTarifs, setTarifsParTerritoire, setTarifsParType, setTarifsParEntreprise, setConfigParTypeBySpace, setDirtyTypeBySpace, setConfigParEntreprise, setConfigParTerritoire, setDemandes, setStats, setTarifDialogOpen, setEditingTarif, setTarifForm, SPACES_TARIF, getAuthHeader, emptyTypeConfig } = deps;

  const fetchConfigTypesAllSpaces = async () => {
    try {
      const response = await axios.get(`${API}/admin/tarifs-config/type`, { headers: getAuthHeader() });
      const all = response.data || [];
      const matrix = SPACES_TARIF.reduce((acc, sp) => {
        acc[sp] = TARIF_TYPES.reduce((typesAcc, t) => {
          const found = all.find(c => c.type_demande === t.value && (c.space || 'default') === sp);
          typesAcc[t.value] = found ? {
            prix_cb: found.prix_cb ?? 15,
            credits: found.credits ?? 5,
            coef_urgence: found.coef_urgence ?? 1.5,
          } : emptyTypeConfig();
          return typesAcc;
        }, {});
        return acc;
      }, {});
      setConfigParTypeBySpace(matrix);
    } catch {
      // silent fallback to empty config
    }
  };
  
  // Charger les configurations par territoire
  const fetchConfigTerritoires = async () => {
    try {
      const response = await axios.get(`${API}/admin/tarifs-config/territoire`, {
        headers: getAuthHeader()
      });
      const configs = response.data || [];
      // Mettre à jour l'état avec les configs sauvegardées
      const configMap = { ...configParTerritoire };
      configs.forEach(c => {
        if (c.territoire) {
          configMap[c.territoire] = {
            coefficient: c.coefficient ?? (c.territoire === 'metropole' ? 1.0 : 0.8),
            remise_speciale: c.remise_speciale ?? 0,
            enabled: c.enabled !== false,
            disable_coef_remise: c.disable_coef_remise === true,
          };
        }
      });
      setConfigParTerritoire(configMap);
    } catch {
    }
  };
  
  // Mettre à jour la configuration par territoire
  const updateConfigTerritoire = (territoireValue, field, value) => {
    setConfigParTerritoire(prev => ({
      ...prev,
      [territoireValue]: {
        ...prev[territoireValue],
        [field]: (field === 'enabled' || field === 'disable_coef_remise') ? !!value : (parseFloat(value) || 0),
      },
    }));
  };
  
  // Sauvegarder configuration par territoire
  const handleSaveConfigTerritoire = async (territoireValue) => {
    try {
      const config = configParTerritoire[territoireValue];
      await axios.post(
        `${API}/admin/tarifs-config/territoire`,
        { territoire: territoireValue, ...config },
        { headers: getAuthHeader() }
      );
      toast.success(`Configuration "${TERRITOIRES.find(t => t.value === territoireValue)?.label}" sauvegardée`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const fetchTarifs = async () => {
    try {
      const response = await axios.get(`${API}/admin/tarifs-demandes`, {
        headers: getAuthHeader()
      });
      const data = response.data || [];
      setTarifs(data.tarifs_generaux || getDefaultTarifs());
      setTarifsParTerritoire(data.tarifs_territoire || []);
      setTarifsParType(data.tarifs_type || []);
      setTarifsParEntreprise(data.tarifs_entreprise || []);
    } catch {
      setTarifs(getDefaultTarifs());
    }
  };

  const fetchDemandes = async () => {
    try {
      const response = await axios.get(`${API}/demandes-publiques`, {
        headers: getAuthHeader()
      });
      setDemandes(response.data || []);
    } catch {
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats-demandes-publiques`, {
        headers: getAuthHeader()
      });
      setStats(response.data || {});
    } catch {
      setStats({
        total_demandes: demandes.length,
        demandes_achetees: 0,
        revenus_total: 0,
        moyenne_par_demande: 0
      });
    }
  };

  const getDefaultTarifs = () => [
    { id: '1', code: 'LEAD_STD', nom: 'Lead Standard', type_demande: 'standard', prix_base: 15, prix_credits: 5, is_active: true },
    { id: '2', code: 'LEAD_BTP', nom: 'Lead BTP', type_demande: 'btp', prix_base: 25, prix_credits: 10, is_active: true },
    { id: '3', code: 'LEAD_MULTI', nom: 'Lead Multi-dispositif', type_demande: 'multi', prix_base: 45, prix_credits: 20, is_active: true },
    { id: '4', code: 'LEAD_PREMIUM', nom: 'Lead Premium', type_demande: 'premium', prix_base: 75, prix_credits: 35, is_active: true },
    { id: '5', code: 'LEAD_URGENT', nom: 'Lead Urgent', type_demande: 'urgent', prix_base: 50, prix_credits: 25, is_active: true }
  ];

  const handleSaveTarif = async () => {
    try {
      const payload = { ...tarifForm };
      
      if (editingTarif) {
        await axios.put(
          `${API}/admin/tarifs-demandes/${editingTarif.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Tarif mis à jour avec succès');
      } else {
        await axios.post(
          `${API}/admin/tarifs-demandes`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Nouveau tarif créé avec succès');
      }
      
      setTarifDialogOpen(false);
      resetTarifForm();
      fetchTarifs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteTarif = async (tarifId) => {
    if (!window.confirm('Supprimer ce tarif ?')) return;
    try {
      await axios.delete(`${API}/admin/tarifs-demandes/${tarifId}`, {
        headers: getAuthHeader()
      });
      toast.success('Tarif supprimé');
      fetchTarifs();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Sauvegarder configuration par type de demande (pour l'espace actif)
  const handleSaveConfigType = async (typeValue) => {
    try {
      const config = configParTypeBySpace[activeSpaceTarif]?.[typeValue] || emptyTypeConfig();
      await axios.post(
        `${API}/admin/tarifs-config/type`,
        { type_demande: typeValue, space: activeSpaceTarif, ...config },
        { headers: getAuthHeader() }
      );
      setDirtyTypeBySpace(prev => ({
        ...prev,
        [activeSpaceTarif]: { ...prev[activeSpaceTarif], [typeValue]: false }
      }));
      const label = TARIF_TYPES.find(t => t.value === typeValue)?.label;
      toast.success(`✓ "${label}" sauvegardée (${activeSpaceTarif})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // Sauvegarder TOUT l'espace actif en une fois
  const handleSaveAllConfigTypeSpace = async () => {
    try {
      const configs = TARIF_TYPES.map(t => ({
        type_demande: t.value,
        space: activeSpaceTarif,
        ...(configParTypeBySpace[activeSpaceTarif]?.[t.value] || emptyTypeConfig()),
      }));
      await axios.post(`${API}/admin/tarifs-config/type/bulk`, configs, { headers: getAuthHeader() });
      setDirtyTypeBySpace(prev => ({ ...prev, [activeSpaceTarif]: {} }));
      toast.success(`✓ Tous les tarifs de l'espace ${activeSpaceTarif} sauvegardés`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde groupée');
    }
  };

  // Cloner depuis l'espace 'default' vers l'espace actif
  const handleCloneFromDefault = async () => {
    if (activeSpaceTarif === 'default') {
      toast.error('Impossible de cloner default vers default');
      return;
    }
    if (!window.confirm(`Recopier tous les tarifs de l'espace "default" vers "${activeSpaceTarif}" ? Les valeurs actuelles seront écrasées.`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/tarifs-config/type/clone`,
        { source: 'default', target: activeSpaceTarif },
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      // Recharge la config de l'espace
      const space = activeSpaceTarif;
      const reload = await axios.get(`${API}/admin/tarifs-config/type?space=${space}`, { headers: getAuthHeader() });
      const reloaded = {};
      TARIF_TYPES.forEach(t => {
        const found = (reload.data || []).find(c => c.type_demande === t.value);
        reloaded[t.value] = found ? {
          prix_cb: found.prix_cb, credits: found.credits, coef_urgence: found.coef_urgence,
        } : emptyTypeConfig();
      });
      setConfigParTypeBySpace(prev => ({ ...prev, [space]: reloaded }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du clonage');
    }
  };

  // Sauvegarder configuration par type d'entreprise
  const handleSaveConfigEntreprise = async (entrepriseValue) => {
    try {
      const config = configParEntreprise[entrepriseValue];
      await axios.post(
        `${API}/admin/tarifs-config/entreprise`,
        { type_entreprise: entrepriseValue, ...config },
        { headers: getAuthHeader() }
      );
      toast.success(`Configuration "${TYPES_ENTREPRISES.find(e => e.value === entrepriseValue)?.label}" sauvegardée`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // Mettre à jour la configuration par type (pour l'espace actif)
  const updateConfigType = (typeValue, field, value) => {
    setConfigParTypeBySpace(prev => ({
      ...prev,
      [activeSpaceTarif]: {
        ...prev[activeSpaceTarif],
        [typeValue]: {
          ...(prev[activeSpaceTarif]?.[typeValue] || emptyTypeConfig()),
          [field]: parseFloat(value) || 0,
        }
      }
    }));
    setDirtyTypeBySpace(prev => ({
      ...prev,
      [activeSpaceTarif]: { ...prev[activeSpaceTarif], [typeValue]: true }
    }));
  };

  // Mettre à jour la configuration par entreprise
  const updateConfigEntreprise = (entrepriseValue, field, value) => {
    setConfigParEntreprise(prev => ({
      ...prev,
      [entrepriseValue]: { ...prev[entrepriseValue], [field]: parseFloat(value) || 0 }
    }));
  };

  const handleToggleTarif = async (tarif) => {
    try {
      await axios.patch(
        `${API}/admin/tarifs-demandes/${tarif.id}/toggle`,
        { is_active: !tarif.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(`Tarif ${tarif.is_active ? 'désactivé' : 'activé'}`);
      fetchTarifs();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDuplicateTarif = (tarif) => {
    // Pré-remplit le formulaire avec les valeurs du tarif source, mais
    // - réinitialise `editingTarif` à null (= mode "Nouveau tarif")
    // - suffixe nom + code pour éviter le doublon (le backend rejette les codes identiques)
    const suffix = ` (copie)`;
    const newCode = `${tarif.code || 'TARIF'}_COPY_${Date.now().toString().slice(-4)}`;
    setEditingTarif(null);
    setTarifForm({
      nom: `${tarif.nom || ''}${suffix}`.trim(),
      code: newCode,
      type_demande: tarif.type_demande || 'standard',
      territoire: tarif.territoire || 'metropole',
      type_entreprise: tarif.type_entreprise || 'all',
      prix_base: tarif.prix_base || 0,
      prix_credits: tarif.prix_credits || 0,
      tva_applicable: tarif.tva_applicable !== false,
      coefficient_urgence: tarif.coefficient_urgence || 1.5,
      coefficient_premium: tarif.coefficient_premium || 2.0,
      remise_volume: tarif.remise_volume || 0,
      remise_fidelite: tarif.remise_fidelite || 0,
      is_active: tarif.is_active !== false,
      description: tarif.description || '',
      territoires_disabled: Array.isArray(tarif.territoires_disabled) ? [...tarif.territoires_disabled] : [],
      types_entreprise_disabled: Array.isArray(tarif.types_entreprise_disabled) ? [...tarif.types_entreprise_disabled] : [],
      taxes_personnalisees: Array.isArray(tarif.taxes_personnalisees) ? [...tarif.taxes_personnalisees] : [],
    });
    setTarifDialogOpen(true);
    toast.success('Tarif dupliqué — personnalisez et enregistrez');
  };

  const openEditDialog = (tarif) => {
    setEditingTarif(tarif);
    setTarifForm({
      nom: tarif.nom || '',
      code: tarif.code || '',
      type_demande: tarif.type_demande || 'standard',
      territoire: tarif.territoire || 'metropole',
      type_entreprise: tarif.type_entreprise || 'all',
      prix_base: tarif.prix_base || 0,
      prix_credits: tarif.prix_credits || 0,
      tva_applicable: tarif.tva_applicable !== false,
      coefficient_urgence: tarif.coefficient_urgence || 1.5,
      coefficient_premium: tarif.coefficient_premium || 2.0,
      remise_volume: tarif.remise_volume || 0,
      remise_fidelite: tarif.remise_fidelite || 0,
      is_active: tarif.is_active !== false,
      description: tarif.description || '',
      territoires_disabled: Array.isArray(tarif.territoires_disabled) ? tarif.territoires_disabled : [],
      types_entreprise_disabled: Array.isArray(tarif.types_entreprise_disabled) ? tarif.types_entreprise_disabled : [],
      taxes_personnalisees: Array.isArray(tarif.taxes_personnalisees) ? [...tarif.taxes_personnalisees] : [],
    });
    setTarifDialogOpen(true);
  };

  const resetTarifForm = () => {
    setEditingTarif(null);
    setTarifForm({
      nom: '',
      code: '',
      type_demande: 'standard',
      territoire: 'metropole',
      type_entreprise: 'all',
      prix_base: 0,
      prix_credits: 0,
      tva_applicable: true,
      coefficient_urgence: 1.5,
      coefficient_premium: 2.0,
      remise_volume: 0,
      remise_fidelite: 0,
      is_active: true,
      description: '',
      territoires_disabled: [],
      types_entreprise_disabled: [],
      taxes_personnalisees: [],
    });
  };

  const getTypeInfo = (type) => {
    return TARIF_TYPES.find(t => t.value === type) || TARIF_TYPES[0];
  };

  const getTerritoireInfo = (territoire) => {
    return TERRITOIRES.find(t => t.value === territoire) || TERRITOIRES[0];
  };

  // V37.25 — Filtres robustes : recherche multi-champs + comparaisons
  // case-insensitive (les territoires en DB peuvent etre 'Guadeloupe' ou
  // 'guadeloupe' selon la source, idem pour les statuts).
  const filteredDemandes = demandes.filter(d => {
    const q = (searchTerm || '').trim().toLowerCase();
    const matchSearch = !q || [
      d.title, d.titre, d.entreprise, d.raison_sociale, d.description,
      d.message, d.nom, d.prenom, d.email, d.oscop_id, d.ville
    ].some(v => typeof v === 'string' && v.toLowerCase().includes(q));

    const norm = (v) => (typeof v === 'string' ? v.toLowerCase().trim() : '');
    const matchTerritoire = selectedTerritoire === 'all'
      || norm(d.territoire) === norm(selectedTerritoire)
      || norm(d.region) === norm(selectedTerritoire);
    const matchType = selectedType === 'all' || norm(d.type_demande) === norm(selectedType);
    const matchStatut = selectedStatut === 'all' || norm(d.statut) === norm(selectedStatut);
    return matchSearch && matchTerritoire && matchType && matchStatut;
  });

  const handleExport = (format) => {
    const dataToExport = tarifs;
    let content, filename, type;
    
    if (format === 'json') {
      content = JSON.stringify({ tarifs: dataToExport, exported_at: new Date().toISOString() }, null, 2);
      filename = `tarifs_demandes_${new Date().toISOString().split('T')[0]}.json`;
      type = 'application/json';
    } else {
      const headers = ['Code', 'Nom', 'Type', 'Prix Base (€)', 'Prix Crédits', 'Actif'];
      const rows = dataToExport.map(t => [t.code, t.nom, t.type_demande, t.prix_base, t.prix_credits, t.is_active ? 'Oui' : 'Non']);
      content = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
      filename = `tarifs_demandes_${new Date().toISOString().split('T')[0]}.csv`;
      type = 'text/csv;charset=utf-8;';
    }
    
    const blob = new Blob(['\uFEFF' + content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Export réussi !');
  };

  // ctx-bag : état + handlers partagés avec les sous-composants extraits

  return { fetchConfigTypesAllSpaces, fetchConfigTerritoires, updateConfigTerritoire, handleSaveConfigTerritoire, fetchTarifs, fetchDemandes, fetchStats, getDefaultTarifs, handleSaveTarif, handleDeleteTarif, handleSaveConfigType, handleSaveAllConfigTypeSpace, handleCloneFromDefault, handleSaveConfigEntreprise, updateConfigType, updateConfigEntreprise, handleToggleTarif, handleDuplicateTarif, openEditDialog, resetTarifForm, getTypeInfo, getTerritoireInfo, filteredDemandes, handleExport };
}
